import { useSelector } from 'react-redux';

import { createSelector } from '@reduxjs/toolkit';

import type { Image, ResourceString } from '@/reducers/common/commonTypes';
import { selectResources } from '@/reducers/common/selectors';

// Types for resources keys maps
export type ResourcesMessageKeys = { [key: string]: string };
export type ResourcesImageKeys = { [key: string]: string };

// Base selector to get full resources from store
const getResourcesData = selectResources as unknown as () => Record<string, string | Image | null>;

// Message selectors
export function getMessageByKeys<T extends ResourcesMessageKeys>(keys: T, isStrictMode = false) {
  return createSelector(
    getResourcesData,
    (data) =>
      Object.entries(keys).reduce((acc, [key, value]) => {
        const val = data?.[value];
        acc[key] = (typeof val === 'string' ? (val as string) : undefined) || (isStrictMode ? '' : value);
        return acc;
      }, {} as ResourcesMessageKeys) as unknown as T,
  );
}

// Image selectors
export function getImagesByKeys<T extends ResourcesImageKeys>(keys: T) {
  return createSelector(
    getResourcesData,
    (data) =>
      Object.entries(keys).reduce(
        (acc, [key, value]) => {
          const val = data?.[value];
          acc[key] = (val as Image) || null;
          return acc;
        },
        {} as Record<string, Image | null>,
      ) as unknown as T,
  );
}

// Hooks
export const useMessages = <T extends ResourcesMessageKeys>(keysMap: T, strict = false): T =>
  useSelector(getMessageByKeys(keysMap, strict)) as unknown as T;

export const useImages = <T extends ResourcesImageKeys>(keysMap: T): T =>
  useSelector(getImagesByKeys(keysMap)) as unknown as T;

// For consumers expecting ResourceString shape
export type { ResourceString };
